'use client'
import * as React from 'react'

import { ClubhousesReactionAction, ClubhousesReactionType } from 'codegen/types'
import { useIsLoggedInQuery } from '@repo/analytics/hooks/use-is-logged-in-query'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { useClubhousesReactToDiscussionMutation } from '@web/gen/clubhouses'

import { useDiscussion } from '../../(social)/discussions/[topicSlug]/[discussionSlug]/_hooks/use-discussion'
import { VoteButton } from '../action-buttons/vote-button'

interface Props {
  upvotes: number
  discussionId: string
  discussionSlug: string
  size?: 'small' | 'regular'
}

export function VoteDiscussion({ discussionId, discussionSlug, size }: Props) {
  const { openModal } = useUserAuthModal()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { refreshDiscussion, discussionData } = useDiscussion(discussionSlug)
  const [localIsLiked, setLocalIsLiked] = React.useState<boolean>(false)
  const [localUpvotes, setLocalUpvotes] = React.useState<number>(0)

  React.useEffect(() => {
    if (discussionData) {
      setLocalIsLiked(!!discussionData.isLiked)
      setLocalUpvotes(discussionData.likesCount)
    }
  }, [discussionData])

  const { mutate: reactToDiscussion, isPending } = useClubhousesReactToDiscussionMutation({
    // oxlint-disable-next-line require-await
    onMutate: async ({ input }) => {
      if (input.reactionAction === ClubhousesReactionAction.Add) {
        setLocalIsLiked(true)
        setLocalUpvotes(prev => prev + 1)
      }
      if (input.reactionAction === ClubhousesReactionAction.Remove) {
        setLocalIsLiked(false)
        setLocalUpvotes(prev => (prev > 0 ? prev - 1 : 0))
      }
    },
    onSuccess: () => {
      refreshDiscussion()
    },
  })

  const handleLikeClick = React.useCallback(() => {
    if (!isLoggedIn) {
      openModal('register')
      {
        return
      }
    }

    reactToDiscussion({
      input: {
        discussionId: +discussionId,
        reactionType: ClubhousesReactionType.Like,
        reactionAction: discussionData?.isLiked
          ? ClubhousesReactionAction.Remove
          : ClubhousesReactionAction.Add,
      },
    })
  }, [isLoggedIn, openModal, discussionId, discussionData?.isLiked, reactToDiscussion])

  return (
    <div
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      className="pointer-events-auto"
    >
      <VoteButton
        size={size}
        isLiked={localIsLiked}
        upvotes={localUpvotes}
        onClick={handleLikeClick}
        isActionLoading={isPending}
      />
    </div>
  )
}
